"use client"

import { useState } from "react"
import Link from "next/link"
import { useRouter, useSearchParams } from "next/navigation"
import { Fingerprint, Loader2, LogIn, ShieldCheck } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { AnimateIn } from "@/components/ui/animate-in"
import { TextMorph } from "@/components/ui/text-morph"
import { signIn, SOCIAL_PROVIDERS } from "@/lib/auth-client"

/**
 * Luma-styled login form — matches the nested-card pattern from
 * /demande-de-ramassage-pdb.
 *
 * Supports:
 *   • email + password
 *   • Passkey (WebAuthn) on supported browsers
 *   • Google / Apple social sign-in (when env creds are set)
 *   • 2FA redirect when the account has TOTP enabled
 */
/**
 * Whitelist of routes the login page is allowed to redirect back to.
 * Anything else becomes /mon-compte — prevents an attacker from
 * stuffing a malicious /api/… redirect or an external URL into the
 * query string and having it fire on successful auth.
 */
const ALLOWED_REDIRECTS = [
  "/mon-compte",
  "/panier",
  "/panier/commander",
  "/panier/commander/merci",
  "/mon-compte/commandes",
  "/mon-compte/profil",
  "/mon-compte/securite",
]

function pickRedirect(raw: string | null): string {
  if (!raw) return "/mon-compte"
  // Must be a same-origin, absolute path.
  if (!raw.startsWith("/") || raw.startsWith("//")) return "/mon-compte"
  if (ALLOWED_REDIRECTS.some((r) => raw === r || raw.startsWith(`${r}?`))) {
    return raw
  }
  return "/mon-compte"
}

export function LoginForm() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const redirectTo = pickRedirect(searchParams.get("redirect"))
  const [loading, setLoading] = useState<
    null | "password" | "passkey" | "google" | "apple"
  >(null)
  const [error, setError] = useState<string | null>(null)
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")

  async function handlePasswordLogin(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading("password")

    const { error } = await signIn.email({
      email,
      password,
      rememberMe: true,
    })

    setLoading(null)

    if (error) {
      setError(error.message || "Email ou mot de passe incorrect.")
      return
    }

    router.push(redirectTo)
    router.refresh()
  }

  async function handlePasskey() {
    setError(null)
    setLoading("passkey")
    try {
      // `autoFill: false` forces the modal; `autoFill: true` uses the inline
      // browser prompt. We default to modal because most users click the btn.
      const { error } = await signIn.passkey({ autoFill: false })
      if (error) throw new Error(error.message)
      router.push(redirectTo)
      router.refresh()
    } catch (e) {
      setError(
        (e as Error).message ||
          "Impossible de se connecter avec votre clé d'accès.",
      )
    } finally {
      setLoading(null)
    }
  }

  async function handleSocial(provider: "google" | "apple") {
    setError(null)
    setLoading(provider)
    try {
      await signIn.social({
        provider,
        callbackURL: redirectTo,
      })
    } catch (e) {
      setError((e as Error).message || "Connexion impossible.")
      setLoading(null)
    }
  }

  return (
    <AnimateIn>
      <div
        className={cn(
          "rounded-[2rem] p-1.5",
          "bg-foreground/[0.02] ring-1 ring-foreground/5",
          "dark:bg-background/[0.06] dark:ring-foreground/10",
        )}
      >
        <div className="rounded-[calc(2rem-0.375rem)] bg-card p-6 md:p-8">
          <div className="mb-6 flex items-center gap-3">
            <div className="flex size-10 items-center justify-center rounded-full bg-primary/10">
              <LogIn className="size-5 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-bold tracking-tight">Se connecter</h2>
              <p className="text-sm text-muted-foreground">
                Accédez à votre compte ADIS 95.
              </p>
            </div>
          </div>

          {error ? (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}

          <form onSubmit={handlePasswordLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email webauthn"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="vous@email.fr"
                required
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Mot de passe *</Label>
                <Link
                  href="/connexion/mot-de-passe-oublie"
                  className="text-xs text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
                >
                  Mot de passe oublié ?
                </Link>
              </div>
              <Input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password webauthn"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={8}
                required
              />
            </div>
            <Button
              type="submit"
              size="lg"
              className="w-full rounded-full"
              disabled={loading !== null}
            >
              {loading === "password" ? (
                <Loader2 className="mr-2 size-4 animate-spin" />
              ) : null}
              <TextMorph as="span" duration={400}>
                {loading === "password" ? "Connexion…" : "Se connecter"}
              </TextMorph>
            </Button>
          </form>

          <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground">
            <div className="h-px flex-1 bg-border" />
            OU
            <div className="h-px flex-1 bg-border" />
          </div>

          <div className="space-y-2">
            <Button
              type="button"
              variant="outline"
              size="lg"
              className="w-full rounded-full"
              onClick={handlePasskey}
              disabled={loading !== null}
            >
              {loading === "passkey" ? (
                <Loader2 className="mr-2 size-4 animate-spin" />
              ) : (
                <Fingerprint className="mr-2 size-4" />
              )}
              Se connecter avec une clé d'accès
            </Button>

            {SOCIAL_PROVIDERS.google && (
              <Button
                type="button"
                variant="outline"
                size="lg"
                className="w-full rounded-full"
                onClick={() => handleSocial("google")}
                disabled={loading !== null}
              >
                <GoogleIcon className="mr-2 size-4" />
                Continuer avec Google
              </Button>
            )}

            {SOCIAL_PROVIDERS.apple && (
              <Button
                type="button"
                variant="outline"
                size="lg"
                className="w-full rounded-full"
                onClick={() => handleSocial("apple")}
                disabled={loading !== null}
              >
                <AppleIcon className="mr-2 size-4" />
                Continuer avec Apple
              </Button>
            )}
          </div>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Pas encore de compte ?{" "}
            <Link
              href={`/creer-compte${redirectTo !== "/mon-compte" ? `?redirect=${encodeURIComponent(redirectTo)}` : ""}`}
              className="font-medium text-primary hover:underline underline-offset-4"
            >
              Créer un compte
            </Link>
          </p>

          <div className="mt-6 flex items-center justify-center gap-2 text-xs text-muted-foreground">
            <ShieldCheck className="size-3.5" />
            Connexion sécurisée.
          </div>
        </div>
      </div>
    </AnimateIn>
  )
}

function GoogleIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" {...props}>
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  )
}

function AppleIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M17.05 20.28c-.98.95-2.05.88-3.08.47-1.09-.42-2.09-.44-3.24 0-1.44.56-2.2.42-3.06-.47C2.79 15.25 3.51 7.59 8.93 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.41 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg>
  )
}
